import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
// import { Loader } from "@googlemaps/js-api-loader";
import { GOOGLE_API_KEY } from "../../clientConfig";
import {
  convertToCapitalize,
  debounceFn,
  getSearchNearByPlace,
} from "../../utils";
import Loading from "../Loader";

interface LocationInputProps {
  name: string;
  label?: string;
  id?: string;
  placeholder?: string;
  type?: string;
  labelClassName?: string;
  className?: string;
  error?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  customElement?: React.ReactNode;
  disabled?: boolean;
  onPlaceSelect: (
    address: string,
    addressDetails: google.maps.places.PlaceResult
  ) => void;
  placeType?: string[];
  searchBy?: string;
  nearByPlace?: {
    types: { type: string; need: number }[];
    radius: number;
  } | null;
  getNearByPlaces?: CallableFunction;
  selectName?: boolean;
  [key: string]: any;
}

const LocationInput: React.FC<LocationInputProps> = ({
  name,
  label,
  id,
  placeholder = "",
  type = "text",
  labelClassName = "",
  className = "",
  error = "",
  value,
  onChange,
  required = false,
  customElement = null,
  disabled = false,
  onPlaceSelect,
  placeType,
  searchBy = "",
  nearByPlace = null,
  getNearByPlaces,
  selectName = false,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const [inputValue, setInputValue] = useState(value || "");
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1); // Track the focused prediction
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const debouncedFetchPredictions = useMemo(
    () =>
      debounceFn((input: string) => {
        if (!autocompleteService) return;
        setFetchingLocation(true);
        if (!input) {
          setFetchingLocation(false);
        }

        autocompleteService.getPlacePredictions(
          {
            input: `${input} ${searchBy}`,

            ...(placeType && { types: placeType }), // Restrict to geocoding results https://developers.google.com/maps/documentation/places/web-service/supported_types#table3
            componentRestrictions: {
              country: "IN",
            }, // Restrict to India
          },
          (predictions, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setPredictions(predictions);
              setShowDropdown(true);
            } else {
              setPredictions([]);
              setShowDropdown(false);
            }
            setFetchingLocation(false);
          }
        );
      }, 500),
    [autocompleteService, placeType, searchBy]
  );

  useEffect(() => {
    import("@googlemaps/js-api-loader").then(({ Loader }) => {
      const loader = new Loader({
        apiKey: GOOGLE_API_KEY,
        version: "weekly",
        libraries: ["places", "drawing"],
      });

      loader.load().then(() => {
        setLoaded(true);
        setAutocompleteService(new google.maps.places.AutocompleteService());
        const hiddenDiv = document.createElement("div");
        setPlacesService(new google.maps.places.PlacesService(hiddenDiv));
      });
    });
  }, []);

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      onChange && onChange(e);
      debouncedFetchPredictions(value);
    },
    [debouncedFetchPredictions]
  );

  // Handle prediction selection
  const handleSelectPrediction = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      if (placesService) {
        placesService.getDetails(
          { placeId: prediction.place_id },
          async (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              onPlaceSelect(place?.formatted_address ?? "", place);
              if (selectName) {
                setInputValue(prediction?.structured_formatting?.main_text);
              } else {
                setInputValue(prediction.description); // Set input value to selected prediction
              }
              setShowDropdown(false); // Hide dropdown
              if (nearByPlace) {
                const nearPlaces = await getSearchNearByPlace(
                  nearByPlace,
                  place?.geometry?.location?.lat() || 0,
                  place?.geometry?.location?.lng() || 0
                );
                getNearByPlaces && getNearByPlaces(nearPlaces);
              }
            }
          }
        );
      }
    },
    [placesService, onPlaceSelect]
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        if (focusedIndex >= 0 && focusedIndex < predictions.length) {
          handleSelectPrediction(predictions[focusedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  return (
    <div className={`form-control location-input`}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required ? <span className="required">*</span> : null}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type={type}
          id={id}
          name={name}
          placeholder={convertToCapitalize(placeholder)}
          value={inputValue}
          onKeyDown={handleKeyDown} // Add keydown event handler
          onChange={handleInputChange}
          className={`${className} ${error ? "!border-[red] error" : ""}`}
          disabled={!loaded || disabled}
          autoComplete="off"
          {...props}
        />
        {customElement}
        {fetchingLocation && (
          <div className="absolute top-3 right-3">
            <Loading size="small" />
          </div>
        )}
        {showDropdown && predictions.length > 0 && (
          <ul
            ref={dropdownRef}
            // className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg"
            className="absolute z-[99999] w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
            role="listbox"
            aria-haspopup="true"
          >
            {predictions.map((prediction, index) => (
              <li
                key={prediction.place_id}
                // className="p-2 cursor-pointer hover:bg-gray-100"
                // onClick={() => handleSelectPrediction(prediction)}
                className={`p-2 cursor-pointer hover:bg-gray-100 ${
                  focusedIndex === index ? "bg-gray-200" : ""
                }`}
                onClick={() => handleSelectPrediction(prediction)}
                onMouseEnter={() => setFocusedIndex(index)} // Set focus on mouse enter
                onMouseLeave={() => setFocusedIndex(-1)} // Reset focus on mouse leave
              >
                {prediction.description}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && (
        <div className="error-message text-[red] absolute top-full ">
          {error}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
